import { useCallback, useEffect, useState } from 'react';
import { User, UserGroup } from '../../../models';
import { apiClient, apiErrorMessage } from '../../../utils';
import { Button, Modal, Table, Typography, Select, Input, message } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons';

export function UserUserGroupsForm(props: { user?: User }) {
  const { Text } = Typography;
  const [allUserGroups, setAllUserGroups] = useState<UserGroup[]>();
  const [userGroups, setUserGroups] = useState<UserGroup[]>();
  const [userGroupsLoading, setUserGroupsLoading] = useState(false);
  const [allUserGroupsLoading, setAllUserGroupsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [selectedUserGroupIds, setSelectedUserGroupIds] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');

  const loadUserGroups = useCallback(() => {
    setUserGroupsLoading(true);
    apiClient
      .get(`user-groups/user/${props.user?.id}`)
      .then((response: any) => {
        setUserGroups(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setUserGroupsLoading(false);
      });
  }, [props.user?.id]);

  useEffect(() => {
    if (props.user?.id) {
      loadUserGroups();
    }
  }, [loadUserGroups, props.user?.id]);

  const loadAllUserGroups = () => {
    if (allUserGroups) return; // Don't reload if already loaded

    setAllUserGroupsLoading(true);
    apiClient
      .get('user-groups')
      .then((response: any) => {
        setAllUserGroups(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setAllUserGroupsLoading(false);
      });
  };

  const handleAddModalOpen = () => {
    setAddModalVisible(true);
    setSelectedUserGroupIds([]);
    loadAllUserGroups();
  };

  const confirmDeleteUserGroup = (userGroupId: string) => {
    Modal.confirm({
      title: 'Kullanıcı grubu sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Kullanıcı grubunu kullanıcıdan silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: () => removeUserGroup(userGroupId),
    });
  };

  const removeUserGroup = (userGroupId: string) => {
    setDeleting(true);
    apiClient
      .delete(`user-groups/${userGroupId}/users/${props.user?.id}`)
      .then(() => {
        message.success('Kullanıcı grubu başarıyla silindi');
        loadUserGroups();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setDeleting(false);
      });
  };

  const addUserGroups = () => {
    if (!selectedUserGroupIds.length) return;

    setSubmitting(true);
    apiClient
      .post(`user-groups/user-assignments`, { userIds: [props.user?.id], userGroupIds: selectedUserGroupIds })
      .then(() => {
        message.success(`${selectedUserGroupIds.length} kullanıcı grubu başarıyla eklendi`);
        setAddModalVisible(false);
        setSelectedUserGroupIds([]);
        loadUserGroups();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const tableColumns = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '',
      key: 'action',
      render: (_: any, record: UserGroup) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <CloseOutlined
            className="text-red-500 cursor-pointer hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => !deleting && confirmDeleteUserGroup(record.id)}
            style={{ opacity: deleting ? 0.5 : 1 }}
            disabled={deleting}
          />
        </div>
      ),
    },
  ];

  const filteredUserGroups = userGroups?.filter(
    userGroup =>
      userGroup.name.toLowerCase().includes(searchText.toLowerCase()) ||
      userGroup.description?.toLowerCase().includes(searchText.toLowerCase()),
  );

  const availableUserGroups = allUserGroups?.filter(
    (userGroup: UserGroup) => !userGroups?.some((assignedUserGroup: UserGroup) => assignedUserGroup.id === userGroup.id),
  );

  return (
    <div>
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddModalOpen}>
          Kullanıcı grubu ekle
        </Button>
        <div>
          <Input
            placeholder="Arama"
            prefix={<SearchOutlined />}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
        </div>
      </div>

      <Table columns={tableColumns} dataSource={filteredUserGroups} rowKey="id" loading={userGroupsLoading} pagination={false} />

      <Modal
        title="Kullanıcı grubu ekle"
        visible={addModalVisible}
        onOk={addUserGroups}
        onCancel={() => {
          setAddModalVisible(false);
          setSelectedUserGroupIds([]);
        }}
        okButtonProps={{ disabled: !selectedUserGroupIds.length, loading: submitting }}
      >
        <Select
          mode="multiple"
          showSearch
          style={{ width: '100%' }}
          placeholder="Kullanıcı seçin"
          onChange={value => setSelectedUserGroupIds(value)}
          value={selectedUserGroupIds}
          loading={allUserGroupsLoading}
          filterOption={(input, option) => {
            const userGroup = availableUserGroups?.find(u => u.id === option?.value);
            if (!userGroup) return false;
            return (
              userGroup.name.toLowerCase().includes(input.toLowerCase()) ||
              userGroup.description?.toLowerCase().includes(input.toLowerCase())
            );
          }}
        >
          {availableUserGroups?.map(userGroup => (
            <Select.Option key={userGroup.id} value={userGroup.id}>
              {userGroup.name}
            </Select.Option>
          ))}
        </Select>
      </Modal>
    </div>
  );
}
